import React, { useEffect, useState } from "react";
import axios from "axios";
import { NavLink, useNavigate } from "react-router-dom";
import { EyeIcon, PencilSquareIcon, TrashIcon, XMarkIcon, DocumentIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import HeadingCp from "../components/HeadingCp";
import SearchBarCP from "../components/SearchBarCP";
import { Nav } from "react-bootstrap";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function UnitPricingEstimateList() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // View modal state
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null); // { header, items }

  // Delete state
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const [baseRows, setBaseRows] = useState([]); // for fallback filtering
  const [searchTerm, setSearchTerm] = useState("");
 

const loadList = async (q = "") => {
  setLoading(true);
  try {
    const url = q
      ? `${apiUrl}/unit_pricing_crud.php?list_estimates=1&q=${encodeURIComponent(q)}`
      : `${apiUrl}/unit_pricing_crud.php?list_estimates=1`;

    const { data } = await axios.get(url, { withCredentials: true });
    const arr = Array.isArray(data) ? data : [];
    setRows(arr);
    setBaseRows(arr);
  } catch (e) {
    console.error(e);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  loadList();
}, [apiUrl]);


  const openView = async (id) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewData(null);
    try {
      const { data } = await axios.get(`${apiUrl}/unit_pricing_crud.php?estimate_detail=${id}`, {
        withCredentials: true,
      });
      if (!data?.header) {
        toast.error("Estimate not found", { position: "top-center" });
        setViewOpen(false);
        return;
      }
      setViewData(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to fetch estimate details", { position: "top-center" });
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const confirmDelete = async (id) => {
    if (!window.confirm(`Delete estimate #${id}? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      const { data } = await axios.post(
        `${apiUrl}/unit_pricing_crud.php`,
        { status: "delete_estimate", estimate_id: id },
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      if (data?.success) {
        toast.success(`Estimate #${id} deleted`, { position: "top-center" });
        setRows((prev) => prev.filter((r) => r.id !== id));
      } else {
        toast.error(data?.error || "Delete failed", { position: "top-center" });
      }
    } catch (e) {
      console.error(e);
      toast.error("Delete failed", { position: "top-center" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);  

  const handleSearchClick = async () => {
    await loadList(searchTerm);

    // fallback to client-side filter if backend doesn't support ?q=
    if (searchTerm && rows.length === baseRows.length) {
      const needle = searchTerm.toLowerCase();
      const filtered = baseRows.filter((r) =>
        [r.company, r.contact_name, r.contact_email, r.contact_phone, r.date, String(r.id)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      );
      setRows(filtered);
    }
  };

  return (
    <div className="mx-auto max-w-[1100px] p-4">
      <HeadingCp label="Unit Pricing Estimates" />
      <div className="mt-5 sm:mt-0 sm flex space-x-3 items-center mb-10">
      <button
        type="button"
        onClick={() => navigate("/unit_pricing_builder")}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
      >
        New Estimate +
      </button>      
      <button
        type="button"
        onClick={() => navigate("/unit_items_admin")}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-400 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
      >
        Edit Unit Price
      </button>      
      <SearchBarCP search={searchTerm} handleChange={handleSearchChange} onClick={handleSearchClick} />      
      </div>
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Company</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2 text-right">Subtotal</th>
              <th className="px-3 py-2 text-right">Tax</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-3 py-2">{r.id}</td>
                <td className="px-3 py-2">{r.company}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.contact_name || "—"}</div>
                  {(r.contact_email || r.contact_phone) && (
                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-2">
                      {r.contact_email && (
                        <a href={`mailto:${r.contact_email}`} className="hover:underline break-all">
                          {r.contact_email}
                        </a>
                      )}
                      {r.contact_email && r.contact_phone && <span>•</span>}
                      {r.contact_phone && (
                        <a href={`tel:${r.contact_phone}`} className="hover:underline">
                          {r.contact_phone}
                        </a>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">{r.date}</td>
                <td className="px-3 py-2 text-right">{fmt(r.subtotal)}</td>
                <td className="px-3 py-2 text-right">{fmt(r.tax)}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(r.total)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {/* View */}
                    <button
                      onClick={() => openView(r.id)}
                      className="p-1 rounded hover:bg-gray-100"
                      title="View"
                    >
                      <EyeIcon className="h-5 w-5 text-gray-700" />
                    </button>
                    {/* Edit */}
                    <button
                      onClick={() => navigate(`/unit-pricing/estimates/${r.id}/edit`)}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-5 w-5 text-indigo-600" />
                    </button>
                    {/* proposal */}
                    <button
                      onClick={() => navigate(`/unit_pricing_proposal/${r.id}`)}
                      className="p-1 rounded hover:bg-gray-100"
                      title="View Proposal"
                    >
                      <DocumentIcon className="h-5 w-5 text-green-600" />
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => confirmDelete(r.id)}
                      className="p-1 rounded hover:bg-gray-100"
                      title="Delete"
                      disabled={deletingId === r.id}
                    >
                      <TrashIcon className={`h-5 w-5 ${deletingId === r.id ? "text-gray-400" : "text-red-600"}`} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-gray-500">
                  {loading ? "Loading…" : "No estimates yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      {viewOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setViewOpen(false)} />
          <div className="absolute inset-x-0 top-10 mx-auto w-full max-w-3xl rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold">
                {viewData?.header ? `Estimate #${viewData.header.id}` : "Estimate Details"}
                <td className="px-3 py-2">
                {viewData?.header ? (
                  <div>
                    <NavLink
                      to={`/unit_pricing_proposal/${viewData.header.id}`}
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                     View proposal
                    </NavLink>
                  </div>
                ) : (
                  "Estimate Link not available"
                )}
              </td>

              </div>
              <button
                onClick={() => setViewOpen(false)}
                className="p-1 rounded hover:bg-gray-100"
                title="Close"
              >
                <XMarkIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {viewLoading && <div className="text-center py-8">Loading details…</div>}
              {!viewLoading && viewData && (
                <>
                  {/* Header block */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-sm">
                    <div>
                      <div className="text-gray-500">Company</div>
                      <div className="font-medium">{viewData.header.company || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Contact</div>
                      <div className="font-medium">{viewData.header.contact_name || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Email</div>
                      <div className="font-medium break-all">{viewData.header.contact_email || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Phone</div>
                      <div className="font-medium">{viewData.header.contact_phone || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Address</div>
                      <div className="font-medium">{viewData.header.company_address || "—"}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Date</div>
                      <div className="font-medium">{viewData.header.date || "—"}</div>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="overflow-x-auto border rounded">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr className="text-left">
                          <th className="px-3 py-2">Cat</th>
                          <th className="px-3 py-2">Device</th>
                          <th className="px-3 py-2 text-right">Unit</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Line</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewData.items || []).map((it) => (
                          <tr key={it.id} className="border-t">
                            <td className="px-3 py-2">{it.cat}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{it.device}</div>
                              {it.description ? <div className="text-gray-500">{it.description}</div> : null}
                            </td>
                            <td className="px-3 py-2 text-right">{fmt(it.unit_price)}</td>
                            <td className="px-3 py-2 text-right">{it.quantity}</td>
                            <td className="px-3 py-2 text-right font-semibold">{fmt(it.line_total)}</td>
                          </tr>
                        ))}
                        {(viewData.items || []).length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                              No items.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-gray-500">Subtotal</div>
                      <div className="font-semibold">{fmt(viewData.header.subtotal)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">
                        Tax ({Number(viewData.header.tax_rate || 0).toFixed(3)}%)
                      </div>
                      <div className="font-semibold">{fmt(viewData.header.tax)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Total</div>
                      <div className="font-extrabold">{fmt(viewData.header.total)}</div>
                    </div>
                  </div>

                  {/* Notes */}
                  {viewData.header.notes && (
                    <div className="mt-4">
                      <div className="text-gray-500 text-sm">Notes</div>
                      <div className="whitespace-pre-wrap bg-gray-50 border rounded p-3">
                        {viewData.header.notes}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t">
              <button
                onClick={() => setViewOpen(false)}
                className="bg-gray-100 px-3 py-2 rounded hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
